// Capacitor-aware auth + persistence helpers. Web behaviour is unchanged:
// `isNativePlatform()` is false in a browser, so all branches short-circuit
// and the existing cookie session keeps working.
//
// In the Capacitor Android/iOS WebView the origin is https://localhost (Android)
// or capacitor://localhost (iOS). The api domain (api.sytofood.com) cannot
// receive the Domain=.sytofood.com session cookie cross-origin, so the native
// client carries the JWT as Authorization: Bearer instead. The token is the
// same JWT the cookie holds; the server echoes it once in X-Session-Token on
// sign-in / sign-up / confirm-password-reset, and we keep it in Capacitor
// Preferences (Android Keystore-backed) between launches.

import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { StatusBar, Style } from "@capacitor/status-bar";

import type { AuthUser } from "./api";

const TOKEN_KEY = "sytofood:sessionToken";
const USER_KEY = "sytofood:cachedUser";

let cachedToken: string | null = null;

export function isNativePlatform(): boolean {
  return Capacitor.isNativePlatform();
}

// Call once on app start, before the first /auth/me. Loads the persisted
// token so the very first request can carry it, and configures the Android
// system bars so the WebView never draws under the status bar (which made
// the syto. logo collide with the clock / battery on the home screen).
export async function initNativeAuth(): Promise<void> {
  if (!isNativePlatform()) return;
  // Status bar: solid brand ink, light icons, no overlay over the WebView.
  // Errors here are non-fatal (older OEM ROMs sometimes refuse).
  try {
    await StatusBar.setOverlaysWebView({ overlay: false });
    await StatusBar.setStyle({ style: Style.Dark });
    await StatusBar.setBackgroundColor({ color: "#16171B" });
  } catch {
    // ignore
  }
  try {
    const { value } = await Preferences.get({ key: TOKEN_KEY });
    cachedToken = value ?? null;
  } catch {
    // Preferences read failed (very rare); fall through to signed-out state.
    cachedToken = null;
  }
}

export function getSessionToken(): string | null {
  return cachedToken;
}

export async function setSessionToken(token: string): Promise<void> {
  cachedToken = token;
  if (!isNativePlatform()) return;
  try {
    await Preferences.set({ key: TOKEN_KEY, value: token });
  } catch {
    // Best-effort; the in-memory copy still lets this session work.
  }
}

export async function clearSessionToken(): Promise<void> {
  cachedToken = null;
  if (!isNativePlatform()) return;
  try {
    await Preferences.remove({ key: TOKEN_KEY });
  } catch {
    // ignore
  }
}

// Headers added to every API call. Web returns an empty record so cookie-only
// auth keeps working unchanged.
export function nativeRequestHeaders(): Record<string, string> {
  if (!isNativePlatform()) return {};
  const headers: Record<string, string> = { "X-Sytofood-Native": "1" };
  if (cachedToken !== null) headers.Authorization = `Bearer ${cachedToken}`;
  return headers;
}

export function fetchCredentials(): RequestCredentials {
  // On native the cookie is cross-origin and rejected; omit it so the Bearer
  // header is the only auth signal. On web we keep `include` so the HttpOnly
  // session cookie continues to flow.
  return isNativePlatform() ? "omit" : "include";
}

// Pull a freshly-issued token out of the response (sign-in / sign-up /
// confirm-password-reset) and persist it. Safe to call on every response.
export async function captureSessionToken(response: Response): Promise<void> {
  if (!isNativePlatform()) return;
  const token = response.headers.get("X-Session-Token");
  if (token !== null && token.length > 0) await setSessionToken(token);
}

// Cache the last successful /auth/me payload so a transient network blip on
// app start does not bounce the user to the sign-in screen. Explicit
// 401/403 still clears the cache; only retries-with-no-network preserve it.
export async function cacheUser(user: AuthUser): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await Preferences.set({ key: USER_KEY, value: JSON.stringify(user) });
  } catch {
    // ignore
  }
}

export async function getCachedUser(): Promise<AuthUser | null> {
  if (!isNativePlatform()) return null;
  try {
    const { value } = await Preferences.get({ key: USER_KEY });
    if (value === null || value === undefined) return null;
    return JSON.parse(value) as AuthUser;
  } catch {
    return null;
  }
}

export async function clearCachedUser(): Promise<void> {
  if (!isNativePlatform()) return;
  try {
    await Preferences.remove({ key: USER_KEY });
  } catch {
    // ignore
  }
}
